import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { tokens as defaultTokens } from '../tokens';

export function NavGlyph({ kind, size = 26, color = 'currentColor', glow = null }) {
  const s = size;
  const style = {
    display: 'block',
    filter: glow ? `drop-shadow(0 0 5px ${glow}aa)` : 'none',
    transition: 'filter 300ms ease',
  };
  const stroke = color;
  const sw = 1.3;

  if (kind === 'home') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M3.5 11.5 L12 4 L20.5 11.5 V20 H14.5 V14 H9.5 V20 H3.5 Z"
          stroke={stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    );
  }
  if (kind === 'stage') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M3 5h18M3 5v14h3V8c2 1 4 1 6 0 2-1 4-1 6 0v11h3V5"
          stroke={stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>
        <path d="M10 19v-8M14 19v-8" stroke={stroke} strokeWidth={sw * 0.8} strokeLinecap="round" opacity="0.5"/>
      </svg>
    );
  }
  if (kind === 'green') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M4 13 V9 a2 2 0 0 1 2 -2 h12 a2 2 0 0 1 2 2 v4"
          stroke={stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>
        <path d="M3 13 a1.5 1.5 0 0 1 1.5 -1.5 h15 A1.5 1.5 0 0 1 21 13 v4 H3 Z"
          stroke={stroke} strokeWidth={sw} strokeLinejoin="round"/>
        <path d="M6 17 v2 M18 17 v2" stroke={stroke} strokeWidth={sw} strokeLinecap="round"/>
      </svg>
    );
  }
  if (kind === 'hours') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M19 15.5A8 8 0 019.5 4 8 8 0 1019 15.5z"
          stroke={stroke} strokeWidth={sw} strokeLinejoin="round"/>
        <circle cx="15.5" cy="7.5" r="0.9" fill={stroke} opacity="0.5"/>
        <circle cx="17" cy="11" r="0.6" fill={stroke} opacity="0.5"/>
      </svg>
    );
  }
  if (kind === 'kit') {
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" style={style} fill="none">
        <path d="M9 7 V6 a2 2 0 0 1 2 -2 h2 a2 2 0 0 1 2 2 v1"
          stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="3.5" y="7" width="17" height="12" rx="1.5"
          stroke={stroke} strokeWidth={sw} strokeLinejoin="round"/>
        <path d="M3.5 11 H20.5" stroke={stroke} strokeWidth={sw} opacity="0.7"/>
        <path d="M11 11 V13 H13 V11" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>
      </svg>
    );
  }
  return null;
}

export const NAV_ITEMS = [
  { id: 'home',  label: 'Home',        glyph: 'home',  path: '/' },
  { id: 'stage', label: 'Stage',       glyph: 'stage', path: '/stage' },
  { id: 'green', label: 'Green Room',  glyph: 'green', path: '/green-room' },
  { id: 'hours', label: 'Ghost Hours', glyph: 'hours', path: '/ghost-hours' },
  { id: 'kit',   label: 'Toolkit',     glyph: 'kit',   path: '/toolkit' },
];

export default function BottomNav({ tokens = defaultTokens, isMod = false, isAdmin = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <nav className="gl-bottom-nav" style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 20,
      paddingBottom: 30,
      background: `linear-gradient(to top, ${tokens.ground} 70%, ${tokens.ground}ee 90%, transparent)`,
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    }}>
      <div style={{ height: 1, background: tokens.edge, opacity: 0.7 }} />

      {/* Mod/Admin links — small text row above nav icons */}
      {(isMod || isAdmin) && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 16,
          padding: '6px 6px 0',
        }}>
          {isMod && (
            <Link to="/mod" style={{
              fontFamily: tokens.fontBody, fontSize: 10, letterSpacing: '0.08em',
              color: location.pathname === '/mod' ? tokens.accent : tokens.textDim,
              textDecoration: 'none', textTransform: 'uppercase',
            }}>Mod</Link>
          )}
          {isAdmin && (
            <Link to="/admin" style={{
              fontFamily: tokens.fontBody, fontSize: 10, letterSpacing: '0.08em',
              color: location.pathname === '/admin' ? tokens.accent : tokens.textDim,
              textDecoration: 'none', textTransform: 'uppercase',
            }}>Admin</Link>
          )}
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
        padding: '12px 6px 10px',
      }}>
        {NAV_ITEMS.map(item => {
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              aria-label={item.label}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                padding: '4px 0',
                position: 'relative',
                textAlign: 'center', minWidth: 0,
                background: 'none', border: 'none', cursor: 'pointer',
              }}
            >
              {/* Active marker dot */}
              <span aria-hidden="true" style={{
                position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)',
                width: 4, height: 4, borderRadius: '50%',
                background: isActive ? tokens.accent : 'transparent',
                boxShadow: isActive ? `0 0 8px ${tokens.accent}` : 'none',
                transition: `all 260ms ${tokens.ease}`,
              }} />
              <NavGlyph kind={item.glyph} size={26}
                color={isActive ? tokens.accent : tokens.textMid}
                glow={isActive ? tokens.accent : null}
              />
              <span style={{
                fontFamily: tokens.fontBody,
                fontSize: 11.5, fontWeight: isActive ? 500 : 400,
                letterSpacing: '0.01em',
                color: isActive ? tokens.textHi : tokens.textLow,
                transition: `color 260ms ${tokens.ease}`,
                whiteSpace: 'nowrap',
                lineHeight: 1,
                position: 'relative',
                width: 0,
                display: 'flex',
                justifyContent: 'center',
              }}>
                <span style={{ transform: 'translateX(-50%)', position: 'absolute', left: 0 }}>
                  {item.label}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
